import React, { useState, useEffect } from "react";
import { useField, useFormikContext, FieldHookConfig } from "formik";
import { startCase, keyBy } from "lodash";
import DatePicker from "react-datepicker";
import { NumericFormat } from "react-number-format";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Clipboard } from "lucide-react";
import { CustomAutosuggest } from "./CustomAutosuggest";
import "react-datepicker/dist/react-datepicker.css";

const Switch = ({ defaultChecked, onChange, label, ...props }) => {
    return (
        <div className="form-check form-switch flex items-center">
            <input className="form-check-input" type="checkbox" defaultChecked={defaultChecked} onChange={onChange} {...props} />
            <label className="form-check-label ml-2">{label}</label>
        </div>
    );
};

import { isValidYYYYMMDDHHMMSS, usePrevious } from "../common/Utils";
import { Button } from "../Button";
import { ComboBox, IComboBoxOption } from "../common/ComboBox";
import { SearchMultipleSelectField, ISelectOption } from "./SearchMultipleSelectField";

export { SearchMultipleSelectField, type ISelectOption };

type NumberInputPropTypes = { field: any; form: any; className: string };

export const NumberInput = ({ field, form, className }: NumberInputPropTypes): React.ReactElement => {
    // const { touched, errors } = form;
    return (
        <NumericFormat
            name={field.name}
            value={typeof field.value !== "undefined" ? field.value : null}
            className={className}
            onValueChange={(e) => form.setFieldValue(field.name, typeof e.floatValue !== "undefined" ? e.floatValue : null, true)}
            type="text"
            thousandSeparator=" "
            decimalSeparator="."
        />
    );
};

export const Checkbox = (props) => <input type="checkbox" {...props} />;

type CheckboxItemPropTypes = {
    name: string;
    defaultValue?: boolean;
    label: string | React.ReactElement;
    className?: string;
    disabled?: boolean;
};

export const CheckboxItem = ({ name, label, className, disabled }: CheckboxItemPropTypes): React.ReactElement => {
    const [field] = useField(name);
    return (
        <div className="mb-4 w-full">
            <Label name={name} value={label} />
            <div className="mt-1 h-9 flex items-center">
                <input
                    id={name}
                    type="checkbox"
                    disabled={disabled}
                    className={`form-check-input h-5 w-5 ${className || ""}`}
                    checked={field.value}
                    {...field}
                />
            </div>
        </div>
    );
};

const instrumentToText = (d): string =>
    [d.name, d.longName !== d.name ? d.longName : null, d.bloombergTicker, d.isin].filter((d) => !!d).join(" ");

type SubmitButtonPropTypes = {
    disabled: boolean;
    label?: string;
    className?: string;
    variant?: "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "ghost";
    size?: "sm" | "md" | "lg";
};

export const SubmitButton = ({
    disabled,
    label = "Save",
    className,
    variant = "primary",
    size = "sm"
}: SubmitButtonPropTypes): React.ReactElement => {
    return (
        <Button type="submit" disabled={disabled} variant={variant} size={size} className={className}>
            {label}
        </Button>
    );
};

type LabelPropTypes = { name: string; value?: string | React.ReactElement };

export const Label = ({ name, value }: LabelPropTypes): React.ReactElement => {
    if (!value) {
        value = startCase(name);
    }
    return <label htmlFor={name}>{value}</label>;
};

type TextFieldPropTypes = FieldHookConfig<string> & { label: any; className: string; disabled: boolean; readOnly?: boolean };

export function TextField({ label, className, disabled, ...props }: TextFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            {typeof label === "string" ? <Label name={field.name} value={label} /> : label}
            <input
                id={field.name}
                //name={field.name}
                className="form-input"
                readOnly={props.readOnly}
                disabled={disabled}
                {...field}
                value={field.value || ""}
                type={props.type || "text"}
                autoComplete={props.autoComplete || undefined}
                spellCheck={props.spellCheck || undefined}
            />
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type NonAutoFillTextFieldPropTypes = FieldHookConfig<string> & { label: string; className: string; disabled: boolean };

export function NonAutoFillTextField({ label, className, disabled, ...props }: NonAutoFillTextFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label} />
            <input
                id={field.name}
                //name={field.name}
                className="form-input auto-complete-off"
                autoComplete="off"
                disabled={disabled}
                {...field}
                value={field.value || ""}
            />
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

interface FieldOptionType {
    key: string;
    value: string;
}

type SelectFieldPropTypes = FieldHookConfig<string> & {
    label?: any;
    className: string;
    disabled: boolean;
    tooltips?: Record<string, string>;
    options: string[] | FieldOptionType[];
    onSelectedChange?: (value: any) => void;
};

export const SelectField = ({
    label,
    className,
    disabled,
    tooltips,
    options,
    onSelectedChange,
    ...props
}: SelectFieldPropTypes): React.ReactElement => {
    const [field, meta] = useField(props);
    const { value } = meta;
    const previousValue = usePrevious(field.value);

    useEffect(() => {
        if (previousValue && previousValue !== value) {
            if (onSelectedChange) {
                onSelectedChange(value);
            }
        }
    }, [value, onSelectedChange, previousValue]);

    let keys: Record<any, any> = {};
    if (options.length > 0) {
        if (typeof options[0] === "object") {
            keys = keyBy(options, "key");
        } else {
            options.forEach((option) => {
                keys[option] = option;
            });
        }
    }

    let exist = false;
    if (field.value in keys) {
        exist = true;
    }

    const allOptions = [...options];
    if (!exist) {
        const newOption = { key: field.value, value: field.value ? field.value : disabled ? "" : "NON EXISTING OPTION" };
        allOptions.unshift(newOption);
    }

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            {typeof label === "undefined" ? (
                <Label name={field.name} />
            ) : typeof label === "string" ? (
                <Label name={field.name} value={label} />
            ) : (
                label
            )}
            <select
                id={field.name}
                //name={field.name}
                className="form-input"
                disabled={disabled}
                {...field}
                value={field.value || ""}
                title={tooltips && field.value && field.value in tooltips ? tooltips[field.value] : undefined}
            >
                {allOptions.map((d, i) => {
                    const key: string = typeof d === "string" ? d : d.key !== null ? d.key : "";
                    return (
                        <option key={i} value={key} title={tooltips && key ? tooltips[key] : undefined}>
                            {typeof d === "string" ? d : d.value}
                        </option>
                    );
                })}
            </select>
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
            {!exist && !disabled ? <div className="form-field-error">'{field.value}' is not an existing option</div> : null}
        </div>
    );
};

type NullableSelectFieldPropTypes = FieldHookConfig<string> & {
    label: any;
    className: string;
    disabled: boolean;
    nullKeyName?: string;
    options: { key: string; value: string }[];
};

export function NullableSelectField({
    label,
    className,
    disabled,
    options,
    nullKeyName,
    ...props
}: NullableSelectFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();

    const exist = options.some((v) => v.key === field.value || !field.value || field.value === "None" || field.value === nullKeyName);

    let arrayOfOptions;
    if (nullKeyName) {
        arrayOfOptions = [{ key: nullKeyName, value: null }, ...options];
    } else {
        arrayOfOptions = [{ key: "None", value: null }, ...options];
    }
    if (!exist) {
        const newOption = { key: field.value, value: field.value ? field.value : "NON EXISTING OPTION" };
        arrayOfOptions.push(newOption);
    }

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            {typeof label === "string" ? <Label name={field.name} value={label} /> : label}
            <select
                id={field.name}
                className="form-input"
                disabled={disabled}
                {...field}
                name={field.name}
                value={field.value === null ? "None" : field.value}
                onChange={(event) => {
                    if (event.target.value === "" || event.target.value === "None") {
                        setFieldValue(field.name, null, true);
                    } else {
                        setFieldValue(field.name, event.target.value, true);
                    }
                }}
            >
                {arrayOfOptions.map((item: { key: string; value: string }) => (
                    <option key={item.key} value={item.value}>
                        {item.key}
                    </option>
                ))}
            </select>
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
            {!exist ? <div className="form-field-error">'{field.value}' is not an existing option</div> : null}
        </div>
    );
}

type PasswordFieldPropTypes = FieldHookConfig<string> & { label: string; className: string; disabled: boolean };

export function PasswordField({ label, className, disabled, ...props }: PasswordFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label} />
            <input
                id={field.name}
                //name={field.name}
                className="form-input"
                disabled={disabled}
                {...field}
                value={field.value || ""}
                type="password"
            />
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type MultipleSelectFieldPropTypes = FieldHookConfig<string[]> & {
    label: string | React.ReactElement;
    className: string;
    disabled: boolean;
    options: any[];
    tooltips?: Record<string, string>;
    size: number;
};

export function MultipleSelectField({
    label,
    className,
    disabled,
    options,
    tooltips,
    size,
    ...props
}: MultipleSelectFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);

    const allKeys = options.map((i) => (typeof i === "string" ? i : i.key));
    const missingValues = field.value ? field.value.filter((v) => !allKeys.includes(v)) : [];

    const missingOptions = missingValues.length
        ? missingValues.map((v) => {
              return { key: v, value: "NOT EXISTING OPTION" };
          })
        : [];

    const allOptions = [...options, ...missingOptions];

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label} />
            <select
                className="form-input"
                id={field.name}
                //name={field.name}
                disabled={disabled}
                {...field}
                multiple
                size={size}
            >
                {allOptions.map((d, i) => {
                    const key = typeof d === "string" ? d : d.key !== null ? d.key : "";
                    const value = typeof d === "string" ? d : d.value;
                    return (
                        <option key={i} value={key} title={tooltips && key ? tooltips[key] : undefined}>
                            {value}
                        </option>
                    );
                })}
            </select>
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
            {missingValues.length > 0 ? <div className="form-field-error">'{missingValues}' is not an existing option</div> : null}
        </div>
    );
}

type NullableMultiSelectFieldPropTypes = FieldHookConfig<string[] | null> & {
    label: string;
    className: string;
    disabled: boolean;
    options: any[];
    tooltips?: Record<string, string>;
    size: number;
};

export function NullableMultiSelectField({
    label,
    className,
    disabled,
    options,
    size,
    tooltips,
    ...props
}: NullableMultiSelectFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();

    const isNullValue = (v: any) => {
        if (!v) {
            return true;
        }
        if (Array.isArray(v) && v.length === 1 && v[0] === "") {
            return true;
        }
        return false;
    };

    const [multiple, setMultipleMode] = useState(!isNullValue(field.value));

    useEffect(() => {
        const isNull = isNullValue(field.value);
        if (isNull && field.value !== null) {
            setFieldValue(field.name, null, true);
        }
        if (!isNull && !Array.isArray(field.value)) {
            setFieldValue(field.name, [field.value], true);
        }
        if (multiple !== !isNull) {
            setMultipleMode(!isNull);
        }
    }, [field.name, field.value, multiple, setFieldValue, setMultipleMode]);

    // add 'null' option
    const nullableOptions = [...[{ key: "", value: "" }], ...options];

    const allKeys = nullableOptions.map((i) => (typeof i === "string" ? i : i.key));
    const missingValues = field.value && Array.isArray(field.value) ? field.value.filter((v) => !allKeys.includes(v)) : [];
    const missingOptions = missingValues.map((v) => {
        return { key: v, value: "NOT EXISTING OPTION" };
    });

    const allOptions = [...nullableOptions, ...missingOptions];

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label} />
            <select
                className="form-input"
                id={field.name}
                //name={field.name}
                disabled={disabled}
                {...field}
                multiple={multiple}
                size={size}
                value={multiple ? field.value || [] : ""}
            >
                {allOptions.map((d, i) => {
                    const key = typeof d === "string" ? d : d.key;
                    const value = typeof d === "string" ? d : d.value;
                    return (
                        <option key={i} value={key} title={tooltips && key ? tooltips[key] : undefined}>
                            {value}
                        </option>
                    );
                })}
            </select>
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
            {missingValues.length > 0 ? <div className="form-field-error">'{missingValues}' is not an existing option</div> : null}
        </div>
    );
}

type TextAreaFieldPropTypes = FieldHookConfig<string> & {
    label: string;
    className: string;
    disabled: boolean;
    rows: number;
    cols: number;
};

export function TextAreaField({ label, className, disabled, rows, cols, ...props }: TextAreaFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label} />
            <textarea className="form-input" id={field.name} disabled={disabled} {...field} rows={rows} cols={cols} value={field.value} />
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type IntegerFieldPropTypes = FieldHookConfig<string> & {
    label: string | React.ReactElement;
    className: string;
    disabled: boolean;
    min: number;
    max: number;
};

export function IntegerField({ label, className, disabled, min, max, ...props }: IntegerFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            {typeof label === "string" ? <Label name={field.name} value={label} /> : label}
            <input
                type="number"
                className="form-input"
                id={field.name}
                disabled={disabled}
                {...field}
                min={min}
                max={max}
                value={field.value == "0" ? field.value : field.value || ""} // '0' is visible
            ></input>
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type DateFieldPropTypes = FieldHookConfig<string> & {
    label: string;
    className: string;
    disabled: boolean;
    minDate?: string;
    maxDate?: string;
};

const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function isValidDateString(s: string): boolean {
    return s === "" || YYYY_MM_DD_REGEX.test(s);
}

export function DateField({ label, className, disabled, minDate, maxDate, ...props }: DateFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();
    const [rawInput, setRawInput] = useState<string | null>(null);

    useEffect(() => {
        setRawInput(null);
    }, [field.value]);

    const thisMinDate = minDate ? new Date(minDate) : null; // is this really correct for all timezones?
    const thisMaxDate = maxDate ? new Date(maxDate) : null; // is this really correct for all timezones?

    const displayValue = rawInput !== null ? rawInput : typeof field.value === "string" ? field.value : "";

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <div className="row-col">
                <Label name={field.name} value={label} />
            </div>
            <div className="row-col">
                <DatePicker
                    name={field.name}
                    calendarStartDay={1}
                    className="form-input"
                    value={displayValue}
                    selected={(field.value && YYYY_MM_DD_REGEX.test(field.value) && new Date(field.value + "T12:00:00")) || null}
                    onChange={(date) => {
                        setRawInput(null);
                        if (date instanceof Date) {
                            //https://github.com/Hacker0x01/react-datepicker/issues/1018#issuecomment-647586438
                            const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                            setFieldValue(field.name, offsetDate.toISOString().slice(0, 10), true);
                        } else {
                            setFieldValue(field.name, "", true);
                        }
                    }}
                    onChangeRaw={(event) => {
                        const raw = event?.target && "value" in event.target ? String((event.target as HTMLInputElement).value) : "";
                        setRawInput(raw);
                        if (isValidDateString(raw)) {
                            setFieldValue(field.name, raw, true);
                            setRawInput(null);
                        }
                        event?.preventDefault?.();
                    }}
                    onBlur={() => {
                        if (rawInput !== null) {
                            setRawInput(null);
                        }
                    }}
                    onSelect={(val) => {
                        if (val instanceof Date) {
                            //https://github.com/Hacker0x01/react-datepicker/issues/1018#issuecomment-647586438
                            const offsetDate = new Date(val.getTime() - val.getTimezoneOffset() * 60000);
                            setFieldValue(field.name, offsetDate.toISOString().slice(0, 10), true);
                        } else {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            setFieldValue(field.name, val.toString().slice(0, 10), true);
                            // sorry I dont believe the type help here
                        }
                    }}
                    strictParsing
                    dateFormat="yyyy-MM-dd"
                    disabled={disabled}
                    minDate={thisMinDate || undefined}
                    maxDate={thisMaxDate || undefined}
                />
            </div>
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type TimestampFieldPropTypes = FieldHookConfig<string> & { label: string; className: string; disabled: boolean };

export function TimestampField({ label, className, disabled, ...props }: TimestampFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label + " (local time)"} />
            <DatePicker
                name={field.name}
                calendarStartDay={1}
                className="form-input"
                selected={
                    isValidYYYYMMDDHHMMSS(field.value)
                        ? new Date(field.value)
                        : field.value && new Date(field.value)
                          ? new Date(new Date(field.value).setHours(12, 0, 0, 0))
                          : null
                }
                onSelect={(val) => {
                    if (val instanceof Date) {
                        setFieldValue(field.name, val.toISOString(), true);
                    } else {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        setFieldValue(field.name, val.toString(), true);
                        // sorry I dont believe the type help here
                    }
                }}
                dateFormat="yyyy-MM-dd HH:mm:ss"
                timeFormat="HH:mm:ss"
                disabled={disabled}
                showTimeInput={true}
            />
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type NullableDateFieldPropTypes = FieldHookConfig<string> & { label: string; className: string; disabled: boolean };
export function NullableDateField({ label, className, disabled, ...props }: NullableDateFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();
    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label} />
            {field.value ? (
                <div className="flex flex-grow">
                    <input
                        id={field.name}
                        type="checkbox"
                        name={field.name}
                        className="me-2"
                        disabled={disabled}
                        checked={field.value ? true : false}
                        onChange={() => {
                            setFieldValue(field.name, null, true);
                        }}
                    />
                    <DatePicker
                        name={field.name}
                        className="form-input"
                        selected={(field.value && new Date(field.value)) || null}
                        onSelect={(val) => setFieldValue(field.name, val ? val.toISOString().slice(0, 10) : null, true)}
                        dateFormat={"yyyy-MM-dd"}
                        disabled={disabled}
                    />
                </div>
            ) : (
                <div className="flex flex-grow">
                    <input
                        id={field.name}
                        type="checkbox"
                        name={field.name}
                        className="me-2"
                        disabled={disabled}
                        checked={field.value ? true : false}
                        onChange={() => {
                            setFieldValue(field.name, new Date().toISOString().slice(0, 10), true);
                        }}
                    />
                    <DatePicker
                        name={field.name}
                        className="form-input"
                        selected={(field.value && new Date(field.value)) || null}
                        onSelect={(val) => setFieldValue(field.name, val ? val.toISOString().slice(0, 10) : null, true)}
                        dateFormat={"yyyy-MM-dd"}
                        disabled={true}
                    />
                </div>
            )}
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type NullableToggleDateFieldPropTypes = FieldHookConfig<string> & { label: string; className?: string; disabled: boolean };
export function NullableToggleDateField({ label, className, disabled, ...props }: NullableToggleDateFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.checked ? new Date().toISOString().slice(0, 10) : null;
        setFieldValue(field.name, newValue, true);
    };

    return (
        <div className={className || ""}>
            {field.value ? (
                <div>
                    <Switch defaultChecked={true} onChange={onChange} label={label} />
                    <div>
                        <DatePicker
                            name={field.name}
                            selected={(field.value && new Date(field.value)) || null}
                            onSelect={(val) => setFieldValue(field.name, val ? val.toISOString().slice(0, 10) : null, true)}
                            dateFormat={"yyyy-MM-dd"}
                            disabled={disabled}
                        />
                    </div>
                </div>
            ) : (
                <div>
                    <Switch defaultChecked={false} label={label} onChange={onChange} />
                </div>
            )}

            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type NumberFieldPropTypes = FieldHookConfig<string> & {
    label: string | React.ReactElement;
    className: string;
    disabled: boolean;
    changeTolerancePercent?: number;
    decimalScale?: number;
    fixedDecimalScale?: boolean;
    allowNegative?: boolean;
};
export function NumberField({
    label,
    className,
    disabled,
    changeTolerancePercent,
    decimalScale,
    fixedDecimalScale,
    allowNegative,
    ...props
}: NumberFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();
    const [oldValue] = useState(field.value ? parseFloat(field.value) : null);
    const [withinChangeTolerance, setWithinChangeTolerance] = useState(changeTolerancePercent ?? true);

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            {typeof label === "string" ? <Label name={field.name} value={label} /> : label}
            <NumericFormat
                name={field.name}
                value={typeof field.value !== "undefined" ? field.value : null}
                className="form-input"
                onValueChange={(e) => {
                    if (typeof e.floatValue === "number") {
                        if (changeTolerancePercent && oldValue && field.value) {
                            const changeTolerance = Math.abs((oldValue * changeTolerancePercent) / 100);
                            const inTolerance = e.floatValue > oldValue - changeTolerance && e.floatValue < oldValue + changeTolerance;
                            setWithinChangeTolerance(inTolerance);
                        }
                        let valueToSet: number | string = e.floatValue;
                        if (typeof decimalScale === "number" && fixedDecimalScale) {
                            valueToSet = e.floatValue.toFixed(decimalScale);
                        }
                        setFieldValue(field.name, valueToSet, true);
                    } else if (!e.floatValue && !e.value.startsWith("-")) {
                        setFieldValue(field.name, e.floatValue, true);
                    }
                }}
                disabled={disabled}
                thousandSeparator=" "
                decimalSeparator="."
                decimalScale={decimalScale}
                fixedDecimalScale={fixedDecimalScale}
                allowNegative={allowNegative}
            />
            {!withinChangeTolerance ? <div className="form-field-error">Note: value is over change tolerance!</div> : null}
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

type InstrumentInputPropTypes = FieldHookConfig<string> & {
    selected: string;
    className: string;
    values: ({ _id: string; name: string; longName: string; isin: string; blombergTicker } & {
        [key: string]: any;
    })[];
    onChange: (
        e: { _id: string; name: string; longName: string; isin: string; blombergTicker } & {
            [key: string]: any;
        }
    ) => void;
};

export const InstrumentInput = ({ selected, className, values, onChange }: InstrumentInputPropTypes): React.ReactElement => {
    return (
        <CustomAutosuggest<
            { _id: string; name: string; longName: string; isin: string; blombergTicker } & {
                [key: string]: any;
            }
        >
            className={className}
            selected={
                values.find((d) => d._id === selected) || {
                    _id: "",
                    name: "",
                    longName: "",
                    isin: "",
                    blombergTicker: null
                }
            }
            values={values}
            toDisplayText={(d) => d.name}
            toSuggestionText={instrumentToText}
            onChange={(val) => {
                if (onChange) {
                    onChange(val);
                }
            }}
        />
    );
};

type OptionType = { _id: string; name: string } & { [key: string]: any };

type SearchListFieldPropTypes = FieldHookConfig<string> & {
    label: any;
    className: string;
    disabled: boolean;
    options: OptionType[];
    onChange?: (e: string) => void;
};

export function SearchListField({ label, className, disabled, options, ...props }: SearchListFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();

    const exist = options.some((v) => v._id === field.value || !field.value);
    const allOptions = [...options];
    if (!exist) {
        const newOption = {
            _id: field.value,
            name: field.value ? field.value : "NON EXISTING OPTION"
        };
        allOptions.push(newOption);
    }

    // ---- If needed, type assertion for generics (optional) ----
    // If your CustomAutosuggest component is generic, assert the props type:
    // Replace 'any' with your CustomAutosuggest prop type if you have one.
    // const CustomAutosuggestTyped = CustomAutosuggest as React.FC<any>;

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            {typeof label === "string" ? <Label name={field.name} value={label} /> : label}
            <CustomAutosuggest
                name={field.name}
                selected={allOptions.find((d) => d._id === field.value) || { _id: "", name: "" }}
                values={allOptions}
                toDisplayText={(d: OptionType) => d.name}
                toSuggestionText={instrumentToText}
                onChange={(val: OptionType | null) => {
                    setFieldValue(field.name, val === null ? null : val._id, true);
                    if (props.onChange) {
                        props.onChange(val === null ? "" : val._id);
                    }
                }}
                disabled={disabled}
            />
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
            {!exist ? <div className="form-field-error">'{field.value}' is not an existing option</div> : null}
        </div>
    );
}

type TransactionOption = {
    _id: string;
    name: string;
    source: string;
    [key: string]: any;
};

type TransactionFieldPropTypes = FieldHookConfig<string> & {
    label: string | React.ReactElement;
    className: string;
    disabled: boolean;
    options: TransactionOption[];
};

const transactionToText = (d: { name: any; source: any }): string => [d.name, d.source].filter((x) => x !== null && x !== "").join(" ");

export function TransactionField({ label, className, disabled, options, ...props }: TransactionFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();

    const exist = options.some((v) => v._id === field.value || !field.value);

    const allOptions = [...options];
    if (!exist) {
        allOptions.push({
            _id: field.value,
            name: field.value ? field.value : "NON EXISTING OPTION",
            source: ""
        });
    }

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            {typeof label === "string" ? <Label name={field.name} value={label} /> : label}
            <CustomAutosuggest
                name={field.name}
                className={""}
                selected={
                    allOptions.find((d) => d._id === field.value) || {
                        _id: "",
                        name: "",
                        source: ""
                    }
                }
                values={allOptions}
                toDisplayText={(d: TransactionOption) => d.name}
                toSuggestionText={transactionToText}
                onChange={(val: TransactionOption | null) => {
                    setFieldValue(field.name, val === null ? null : val._id, true);
                }}
                disabled={disabled}
            />
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
            {!exist ? <div className="form-field-error">'{field.value}' is not an existing option</div> : null}
        </div>
    );
}

type CheckBoxFieldPropTypes = FieldHookConfig<any> & {
    label: string;
    className: string;
    disabled: boolean;
};

export function CheckBoxField({ label, className, disabled, ...props }: CheckBoxFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <div className="flex items-center gap-2">
                <input
                    id={field.name}
                    type="checkbox"
                    //name={field.name}
                    className="form-checkbox"
                    disabled={disabled}
                    checked={field.value}
                    {...field}
                    value={field.value || ""}
                />
                <Label name={field.name} value={label} />
            </div>
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

export function compareCurrency(c1, c2): number {
    const hotList = ["SEK", "EUR", "USD", "NOK", "DKK", "GBP"].reduce((dict, c, i) => {
        dict[c] = i + 1;
        return dict;
    }, {});
    if (hotList[c1]) {
        return hotList[c2] ? hotList[c1] - hotList[c2] : -1;
    }
    if (hotList[c2]) {
        return 1;
    }
    return c1 < c2 ? -1 : c1 > c2 ? 1 : 0;
}

export { CustomAutosuggest };

type CopyClipboardButtonPropTypes = { id: string; text: string };

export function CopyClipboardButton({ id, text }: CopyClipboardButtonPropTypes): React.ReactElement {
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const handleCopy = () => {
        if (window["clipboardData"] && window["clipboardData"].setData) {
            // Internet Explorer-specific code path to prevent textarea being shown while dialog is visible.
            setTooltipOpen(true);
            setTimeout(() => {
                setTooltipOpen(false);
            }, 2000);
            return window["clipboardData"].setData("Text", text);
        } else if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
            const textarea = document.createElement("textarea");
            textarea.textContent = text;
            textarea.style.position = "fixed"; // Prevent scrolling to bottom of page in Microsoft Edge.
            document.body.appendChild(textarea);
            textarea.select();
            try {
                return document.execCommand("copy"); // Security exception may be thrown by some browsers.
            } catch (ex) {
                console.warn("Copy to clipboard failed.", ex);
                return false;
            } finally {
                setTooltipOpen(true);
                setTimeout(() => {
                    setTooltipOpen(false);
                }, 2000);
                document.body.removeChild(textarea);
            }
        }
    };

    return (
        <Tooltip.Provider>
            <Tooltip.Root open={tooltipOpen} onOpenChange={setTooltipOpen}>
                <Tooltip.Trigger asChild>
                    <button
                        id={id}
                        type="button"
                        className="px-2 py-1 text-sm bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors float-right"
                        title="Copy to Clipboard"
                        onClick={handleCopy}
                    >
                        <Clipboard className="w-4 h-4" />
                    </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content className="px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg z-50" side="bottom">
                        Copied!
                        <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </Tooltip.Provider>
    );
}

type JsonFieldPropTypes = FieldHookConfig<string> & {
    label: string;
    className: string;
    rows?: number;
};

export function JsonField({ label, className, rows = 15, ...props }: JsonFieldPropTypes): React.ReactElement {
    const [field, meta, helpers] = useField(props);
    const { setFieldValue } = useFormikContext();

    const validateJson = (value: string) => {
        try {
            JSON.parse(value);
            return undefined; // no error
        } catch (_e) {
            return "Invalid JSON format";
        }
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        const error = validateJson(event.target.value);
        helpers.setError(error);
        if (!error) {
            helpers.setValue(JSON.parse(event.target.value)); // Update the form value with the parsed JSON object
        }
        field.onBlur(event); // Call the default onBlur handler
    };

    const handleChange = (e) => {
        const newValue = e.target.value;

        if (typeof newValue === "string") {
            try {
                const o = JSON.parse(newValue);
                setFieldValue(field.name, o, true);
                return undefined; // no error
            } catch (_e) {
                setFieldValue(field.name, newValue, true);
            }
        } else {
            setFieldValue(field.name, newValue, true);
        }
    };

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label} />
            <textarea
                className={`form-input ${meta.error ? "is-invalid border-red-500" : ""}`}
                id={field.name}
                name={field.name}
                rows={rows}
                value={typeof field.value === "string" ? field.value : JSON.stringify(field.value, null, 2)}
                onBlur={handleBlur} // Override the onBlur to validate and parse JSON
                onChange={handleChange} // Default onChange handler
            />
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}

interface MaybeInListFieldOptionType {
    key: string;
    value: string;
}
type MaybeInListFieldPropTypes = FieldHookConfig<string> & {
    label?: any;
    className: string;
    disabled: boolean;
    options: MaybeInListFieldOptionType[];
};

export function MaybeInListField({ label, className, disabled, options, ...props }: MaybeInListFieldPropTypes): React.ReactElement {
    const [field, meta] = useField(props);
    const { setFieldValue } = useFormikContext();

    const comboOptions: IComboBoxOption[] = options.map((o) => {
        return { _id: o.key, name: o.key, text: o.value };
    });

    // update options
    if (field.value && !comboOptions.map((o) => o.name).includes(field.value)) {
        comboOptions.push({ _id: field.value, name: field.value, text: field.value });
    }

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <Label name={field.name} value={label} />
            <ComboBox
                className="w-100"
                options={comboOptions}
                defaultValue={field.value || ""}
                onOptionSelect={(option: IComboBoxOption) => {
                    setFieldValue(field.name, option.text, true);
                }}
                onChange={(value) => {
                    setFieldValue(field.name, value, true);
                }}
                disabled={disabled}
            ></ComboBox>
            {meta.error ? <div className="form-field-error">{meta.error}</div> : null}
        </div>
    );
}
